import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Send, X } from 'lucide-react';
import { createInvitation } from '../../services/invitationService';
import { InvitationCreate } from '../../types';
import { notify } from '../../lib/notify';

interface InvitationFormProps {
  organizationId: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const InvitationForm: React.FC<InvitationFormProps> = ({ organizationId, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate email
    if (!email.trim()) {
      setError(t('invitations.errors.emailRequired'));
      return;
    }

    if (!validateEmail(email)) {
      setError(t('invitations.errors.emailInvalid'));
      return;
    }

    setLoading(true);

    try {
      const invitationData: InvitationCreate = {
        email: email.trim(),
        organization_id: organizationId
      };

      await createInvitation(invitationData);
      
      notify.success(t('invitations.invitationSent'));
      setEmail('');
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Error creating invitation:', err);
      
      // Handle specific error messages
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (detail.includes('already exists')) {
          setError(t('invitations.errors.emailAlreadyExists'));
        } else if (detail.includes('pending invitation')) {
          setError(t('invitations.errors.pendingInvitationExists'));
        } else {
          setError(detail);
        }
      } else {
        setError(t('invitations.errors.sendFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center mb-4">
        <Mail className="w-5 h-5 text-indigo-600 mr-2" />
        <h2 className="text-lg font-semibold text-gray-900">
          {t('invitations.inviteUser')}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            {t('invitations.emailLabel')}
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError(null);
            }}
            placeholder={t('invitations.emailPlaceholder')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            disabled={loading}
          />
          {error && (
            <p className="mt-1 text-sm text-red-600">{error}</p>
          )}
        </div>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={loading || !email.trim()}
            className="flex-1 flex items-center justify-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t('common.sending', 'Envoi...')}
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                {t('invitations.send')}
              </>
            )}
          </button>
          
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:bg-gray-50 disabled:cursor-not-allowed transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default InvitationForm;
